import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Loader } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import type { AdCreative } from '../types';

interface BulkAdCreativeReleaseModalProps {
  creatives?: AdCreative[];
  creativeIds?: string[];
  artistId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const BulkAdCreativeReleaseModal: React.FC<BulkAdCreativeReleaseModalProps> = ({
  creatives,
  creativeIds: propCreativeIds,
  artistId,
  onClose,
  onSuccess
}) => {
  // Support both: full creative objects or just IDs
  const allCreativeIds = propCreativeIds || (creatives ? creatives.map(c => c.id) : []);
  const totalCount = allCreativeIds.length;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [releases, setReleases] = useState<any[]>([]);
  const [selectedRelease, setSelectedRelease] = useState<string>('');
  const [loadingReleases, setLoadingReleases] = useState(true);

  // Fetch releases for the artist
  useEffect(() => {
    const fetchReleases = async () => {
      if (!artistId) {
        setLoadingReleases(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('releases')
          .select('id, name, release_date, created_at')
          .eq('artist_id', artistId as any)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setReleases((data || []) as any[]);
      } catch (error) {
        console.error('Error fetching releases:', error);
        setError('Failed to load releases');
      } finally {
        setLoadingReleases(false);
      }
    };

    fetchReleases();
  }, [artistId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (loading) return;

    setLoading(true);
    setError(null);

    try {
      console.log('Bulk updating', allCreativeIds.length, 'ad creatives with release:', selectedRelease || null);
      
      // Update in batches of 100 to avoid query limits
      for (let i = 0; i < allCreativeIds.length; i += 100) {
        const batch = allCreativeIds.slice(i, i + 100);
        const { error: updateError } = await supabase
          .from('ad_creatives')
          .update({ 
            release_id: selectedRelease || null,
            updated_at: new Date().toISOString()
          } as any)
          .in('id' as any, batch as any);

        if (updateError) {
          console.error('Supabase update error:', updateError);
          throw updateError;
        }
      }

      console.log('Bulk update successful');
      
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating ad creatives:', err);
      setError(err instanceof Error ? err.message : 'Failed to update ad creatives. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 20 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: {
        type: "spring",
        duration: 0.5,
        bounce: 0.3
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.9, 
      y: 20,
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div 
        className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full p-6"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold dark:text-white">
            Assign Release to {totalCount} Ad Creative{totalCount > 1 ? 's' : ''}
          </h2>
          <motion.button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            disabled={loading}
          >
            <X className="h-5 w-5" />
          </motion.button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <AnimatePresence>
            {error && (
              <motion.div 
                className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-md"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
              >
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <p>{error}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Selected creatives list */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <label className="block text-sm font-medium mb-2 dark:text-white">
              Selected Ad Creatives
            </label>
            <div className="max-h-32 overflow-y-auto bg-gray-50 dark:bg-gray-700 rounded-md p-2 space-y-1">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {totalCount} ad creative{totalCount > 1 ? 's' : ''} selected
              </div>
              {creatives && creatives.slice(0, 10).map(creative => (
                <div key={creative.id} className="text-sm text-gray-600 dark:text-gray-300 truncate">
                  • {creative.videoName || creative.content.substring(0, 50) + '...'}
                </div>
              ))}
              {totalCount > 10 && (
                <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                  ... and {totalCount - (creatives ? Math.min(creatives.length, 10) : 0)} more
                </div>
              )}
            </div>
          </motion.div>

          {/* Release Selection */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <label className="block text-sm font-medium mb-1 dark:text-white">
              Release Assignment
            </label>
            {loadingReleases ? (
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <Loader className="h-4 w-4 animate-spin" />
                <span>Loading releases...</span>
              </div>
            ) : releases.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No releases found for this artist. Create a release first.
              </p>
            ) : (
              <select
                value={selectedRelease}
                onChange={(e) => setSelectedRelease(e.target.value)}
                className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-primary-500 focus:ring-primary-500"
                disabled={loading}
              >
                <option value="">No Release Assigned</option>
                {releases.map((release) => (
                  <option key={release.id} value={release.id}>
                    {release.name}
                    {release.release_date 
                      ? ` (${new Date(release.release_date).toLocaleDateString('de-DE', { 
                          day: 'numeric', 
                          month: 'numeric', 
                          year: 'numeric' 
                        })})` 
                      : ''}
                  </option>
                ))}
              </select>
            )}
          </motion.div>

          <motion.div 
            className="flex justify-end space-x-2 pt-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <motion.button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              disabled={loading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Cancel
            </motion.button>
            <motion.button
              type="submit"
              disabled={loading || loadingReleases || releases.length === 0}
              className="btn disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              whileHover={!loading ? { scale: 1.05 } : {}}
              whileTap={!loading ? { scale: 0.95 } : {}}
            >
              {loading ? (
                <>
                  <Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  Updating...
                </>
              ) : (
                'Assign Release'
              )}
            </motion.button>
          </motion.div>
        </form>
      </motion.div>
    </div>
  );
};
